/**
 * API-FUNCTION BLOCKS -- one `<name>_api_functions` block + generator per registered API function.
 *
 * Extracted verbatim from blockly-interface.component.ts `createDefinitionsForFunctions()`. These are
 * DYNAMIC: the set depends on the list Blockly loads (`functionsService.query(500)`). So this takes the ALREADY-QUERIED
 * list rather than the service -- which is what makes it usable headless, with no DI and no Angular.
 *
 * The emitted code calls `this.functionService.call(...)`. That `this` is the RUNTIME page component,
 * not this module and not the editor component. Do not "fix" it.
 *
 * NOTHING REMOVED -- the component still owns the query and calls here.
 * See docs/plans/background-generation-pipeline.md.
 */
import * as Blockly from 'blockly-tradais';
import { javascriptGenerator } from 'blockly-tradais/javascript';

/** @param funcs the list Blockly loads with `functionsService.query(500)`. Pass `[]` for none. */
export function registerApiFunctionBlocks(funcs: any[] | undefined | null) {
    funcs?.forEach((func) => {
      Blockly.Blocks[func.name + '_api_functions'] = {
        init: function () {
          // A flow reads as what it does; its type stays <name>_api_functions, which every saved page already holds.
          this.appendDummyInput().appendField(func.type === 'BASTION' ? `Run flow: ${func.name}` : func.name + '_api_functions');
          for (var input of func.inputs) {
            this.appendValueInput(input.name)
              .setCheck(input.type)
              .appendField(input.name);
          }
          for (var output of func.outputs) {
            this.appendValueInput(output.name)
              .setCheck(output.type)
              .appendField(output.name);
          }
          this.setPreviousStatement(true, null);
          this.setNextStatement(true, null);
          this.setColour('#a61580');
        },
      };
      (javascriptGenerator as any).forBlock[func.name + '_api_functions'] = function (block: any) {
        var code = `const inputs = {};`;
        for (var input of func.inputs) {
          // An empty socket gives '' — writing `inputs['x'] = ;` broke the whole page function. Leaving the key out lets
          // the callee apply the input's default (a flow does; a missing required input is its own clear error).
          const inputCode = (javascriptGenerator as any).valueToCode(
            block,
            input.name,
            (javascriptGenerator as any).ORDER_ASSIGNMENT
          );
          if (inputCode) code += `\ninputs['${input.name}'] = ${inputCode};`;
          // code += `\ninputs['${input.name}'] = ${(
          //   javascriptGenerator as any
          // ).valueToCode(
          //   block,
          //   input.name,
          //   (javascriptGenerator as any).ORDER_ASSIGNMENT
          // )};`;
        }
        code += `\n const output = await this.functionService.call('${func.name.replace('_api_functions','')}', inputs , ${func.type ? JSON.stringify({ type: func.type, webhookUrl: func.webhookUrl }) : func.internalFunctionFlag});`;
        for (var output of func.outputs) {
          const outputCode = (javascriptGenerator as any).valueToCode(
            block,
            output.name,
            (javascriptGenerator as any).ORDER_ASSIGNMENT
          );
          // An empty output socket has nowhere to put the value; assigning to '' would not parse.
          if (outputCode && !outputCode.includes('NAN')) {
          // if(!(javascriptGenerator as any).valueToCode(
          //   block,
          //   output.name,
          //   (javascriptGenerator as any).ORDER_ASSIGNMENT
          // ).includes('NAN')) {
            // console.log((javascriptGenerator as any).valueToCode(
            //   block,
            //   output.name,
            //   (javascriptGenerator as any).ORDER_ASSIGNMENT
            // ));
            const variableName =(javascriptGenerator as any).valueToCode(
            block,
            output.name,
            (javascriptGenerator as any).ORDER_ASSIGNMENT
          );
          code += `\n if(output['${
            output.name
          }'] !== null && output['${output.name}'] !== undefined){ 
          const id = ${variableName}${(output.type === 'ENTITY' && ( variableName.includes('localVariable$') || variableName.includes('globalVariable$'))) ? '$data' : ''}$id;
          const auto = ${variableName}${(output.type === 'ENTITY' && ( variableName.includes('localVariable$') || variableName.includes('globalVariable$'))) ? '$data' : ''}$id_auto_increment;
          ${ variableName }${(output.type === 'ENTITY' && ( variableName.includes('localVariable$') || variableName.includes('globalVariable$'))) ? '$data' : ''} = output['${
            output.name
          }'];
          if(id && id!=='') {
          ${variableName}${(output.type === 'ENTITY' && ( variableName.includes('localVariable$') || variableName.includes('globalVariable$'))) ? '$data' : ''}$id = id;
          }
          if(auto && auto!=='') {
          ${variableName}${(output.type === 'ENTITY' && ( variableName.includes('localVariable$') || variableName.includes('globalVariable$'))) ? '$data' : ''}$id_auto_increment = auto;
          }
          }
          `;
        }
        }
        return code;
      };
    });
}
